<template>
  <div :data-parameter="$parent.sectionKey ? paramKey : ''" class="parameter-row">
    <div class="parameter-pulse-overlay"></div>
    <div :class="[getParameterClass(), getHeaderClass()]" class="parameter-info">
      <v-row class="parameter-header">
        <v-col class="align-content-space" cols="10">

          <v-btn
            :class="[ 'button-checklist-status-change', 'float-left','mr-1', {'clickable': !readonly, 'readonly': readonly}]"
            :color="getStatusColor()"
            small
            text
            title="Cambiar estado de comprobación"
            @click="!readonly && cycleParameterValue()">
            <v-icon
              :color="getStatusColor()"
              class="mr-1"
              small
            >
              {{ getStatusIcon() }}
            </v-icon>
            <span>
            {{ indexSection + 1
              }}.{{ indexParameter + 1
              }}</span>
          </v-btn>
          <div class="float-left param-text">
          <span class="font-weight-medium"> {{ parameter.name
            }}.</span>
            <span v-if="parameter.value === 'checked' || parameter.value == 'not_required'"
                  class="ml-2">({{ formatTimestamp(parameter.timestamp) + ' - ' + parameter.user.split('@')[0]
              }})</span>
            <span v-if="parameter.value === 'pending'"
                  class="ml-1 font-weight-regular   contrast_high--text "> {{ parameter.description }}</span>
            <a v-if="parameter.documentationLink" :href="parameter.documentationLink" class="ml-2 font-weight-medium"
               target="_blank">(+ info)</a>
            <v-tooltip v-if="hasValueChanges" top>
              <template v-slot:activator="{ on, attrs }">
                <v-chip
                  class="ml-2"
                  color="error"
                  label
                  small
                  text-color="white"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon left x-small>warning</v-icon>
                  CAMBIO DETECTADO
                </v-chip>
              </template>
              <span>Los valores actuales del proceso han cambiado desde la última validación</span>
            </v-tooltip>
            <v-row v-if="parameter.comment" class="comment-row mt-1">
              <v-col class="py-1" cols="12">
                <v-chip class="comment-inline-chip font-weight-regular" label small>
                  <v-icon class="mr-1" small>comment</v-icon>
                  Comentario: <strong>{{ parameter.comment }}</strong>
                </v-chip>
              </v-col>
            </v-row>
          </div>

        </v-col>
        <v-col class="text-right align-content-center" cols="2">
          <div class="parameter-actions d-flex align-center justify-end">
            <!-- Botón de comentario con menú siguiendo la lógica de selección -->
            <v-menu
              v-if="!isCompleted"
              absolute
              left
              min-width="400"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  class="mr-2 comment-btn"
                  color="primary"
                  icon
                  outlined
                  title="Añadir comentario"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon small>comment</v-icon>
                </v-btn>
              </template>
              <v-card>
                <v-card-text class="px-2 pb-0">
                  <p>Escribir comentario para el parámetro</p>
                  <v-textarea
                    v-model="commentText"
                    hide-details
                    outlined
                    placeholder="Comentario..."
                    rows="3"
                    @click.stop
                  />
                </v-card-text>
                <v-card-actions>
                  <v-btn
                    :disabled="commentText === ''"
                    block
                    color="primary"
                    height="42px"
                    small
                    @click="saveComment"
                  >
                    <span>Guardar</span>
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-menu>
            <!-- Botón de información (siempre interactuable incluso en readonly) -->
            <v-btn
              v-if="hasExtraInfo"
              :color="hasValueChanges ? 'error':'primary'"
              class="mr-2 info-btn"
              icon
              outlined
              @click="showExtraInfo = !showExtraInfo"
            >
              <v-icon small>{{ showExtraInfo ? 'expand_less' : 'info' }}</v-icon>
            </v-btn>


            <!-- Toggle de status -->
            <ChecklistStatusToggle
              v-if="!readonly"
              :available-values="availableValues"
              :value="parameter.value"
              @change="$emit('update-value', $event)"
              @validate-custom-check="validateCustomCheck"
            />
          </div>
        </v-col>
      </v-row>

      <v-row v-if="showExtraInfo && hasExtraInfo" class="mt-2">
        <v-col class="pr-3">
          <div class="parameter-extra-info">
            <div v-if="hasCustomCheckMethod" class="custom-check-method-container mb-2">
              <component
                :is="parameter.customCheckMethod"
                ref="customCheckComponent"
                :process="processInfo"
                :readonly="readonly"
              ></component>
            </div>
            <div v-else>
              <process-variables
                :parameter="parameter"
                :process-info="processInfo"
                :readonly="readonly"
              />
            </div>
          </div>
          <!-- Comentario en extra info siguiendo la lógica -->
          <div v-if="parameter.comment" class="comment-extra-info mt-2">
            <div class="extra-info-header">
              <v-icon class="mr-1" size="14">comment</v-icon>
              <span class="extra-info-title">Comentario</span>
            </div>
            <div class="comment-content">
              <span class="comment-text">{{ parameter.comment }}</span>
            </div>
          </div>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
  import ChecklistStatusToggle from './ChecklistStatusToggle'
  import '@/components/management/processManagement/components/checklist/checkMethods'
  import vuex_authentication from '@/store/modules/vuex_authentication'
  import ProcessVariables from './checkMethods/ProcessVariables'
  import checklistService from '@/services/checklistService'

  export default {
    name: 'ChecklistParameter',
    components: {
      ChecklistStatusToggle,
      ProcessVariables
    },
    props: {
      parameter: {
        type: Object,
        required: true
      },
      readonly: {
        type: Boolean,
        default: false
      },
      availableValues: {
        type: Array,
        default: () => ['pending', 'checked', 'not_required']
      },
      processInfo: {
        type: Object,
        default: null
      },
      indexSection: {
        type: Number,
        default: 0
      },
      indexParameter: {
        type: Number,
        default: 0
      }
    },
    data() {
      return {
        commentText: '',
        showExtraInfo: false,
        showRevalidateDialog: false,
        paramKey: '', // Para almacenar la clave del parámetro
        // Ciclo de valores para el cambio de estado
        valuesCycle: ['pending', 'checked', 'not_required']
      }
    },
    mounted() {
      // Guardar la clave del parámetro extrayéndola del DOM padre
      const parentElement = this.$el.parentElement
      if (parentElement && parentElement.hasAttribute('data-parameter')) {
        this.paramKey = parentElement.getAttribute('data-parameter')
      }
      this.commentText = this.parameter.comment || ''
    },
    computed: {
      isCompleted() {
        return this.parameter.value === 'checked' || this.parameter.value === 'not_required'
      },

      isDisabled() {
        return this.parameter.value === 'not_required'
      },

      hasExtraInfo() {
        return (this.parameter.extraInfo && Object.keys(this.parameter.extraInfo).length > 0) || this.hasCustomCheckMethod
      },

      hasCustomCheckMethod() {
        return this.parameter.customCheckMethod && this.parameter.customCheckMethod.trim() !== ''
      },

      hasValueChanges() {
        if (this.parameter.value !== 'checked' || !this.hasExtraInfo || !this.processInfo) {
          return false
        }

        return checklistService.parameterHasValueChanges(this.parameter, this.processInfo)
      }
    },
    watch: {
      'parameter.comment'(newValue) {
        this.commentText = newValue || ''
      }
    },
    methods: {
      // Método para ciclar el valor del parámetro
      cycleParameterValue() {
        // Si está en modo readonly, no permitir cambios
        if (this.readonly) return

        const currentIndex = this.valuesCycle.indexOf(this.parameter.value)
        const nextIndex = (currentIndex + 1) % this.valuesCycle.length
        const nextValue = this.valuesCycle[nextIndex]

        // Si el siguiente valor es 'checked', necesitamos validar el custom check si existe
        if (nextValue === 'checked') {
          this.validateCustomCheck(nextValue, (isValid) => {
            if (isValid) {
              this.$emit('update-value', nextValue)
            }
            // Si no es válido, no hacemos nada y mantenemos el valor actual
          })
        } else {
          // Para 'pending' y 'not_required' no necesitamos validación
          this.$emit('update-value', nextValue)
        }
      },

      getStatusColor() {
        if (this.hasValueChanges) return 'error'
        return checklistService.getParameterIconColor(this.parameter.value)
      },

      getStatusIcon() {
        if (this.hasValueChanges) return 'warning'
        return checklistService.getParameterIcon(this.parameter.value)
      },

      getParameterClass() {
        const classes = {
          'checked': 'parameter-info-checked',
          'not_required': 'parameter-info-not-required',
          'pending': 'parameter-info-pending'
        }
        return classes[this.parameter.value] || ''
      },

      getHeaderClass() {
        return ''
      },

      getProcessValue(field) {
        return checklistService.getProcessValue(this.processInfo, field)
      },

      getValueChanges() {
        return checklistService.getValueChanges(this.parameter, this.processInfo)
      },

      revalidateParameter() {
        if (this.parameter.extraInfo && this.processInfo) {
          Object.keys(this.parameter.extraInfo).forEach(field => {
            const currentValue = this.getProcessValue(field)
            if (currentValue !== 'N/A') {
              this.parameter.extraInfo[field] = currentValue
            }
          })

          this.parameter.timestamp = this.$moment().unix()
          this.parameter.user = this.$store.state[vuex_authentication.constants.NAMESPACE][vuex_authentication.constants.S_USERNAME]
          this.$emit('parameter-revalidated', this.parameter)
        }

        this.showRevalidateDialog = false

        if (this.$toast) {
          this.$toast.success('Parámetro actualizado con los valores actuales del proceso')
        }
      },

      validateCustomCheck(newValue, callback) {
        if (!this.hasCustomCheckMethod || !this.$refs.customCheckComponent) {
          callback(true)
          return
        }

        try {
          if (typeof this.$refs.customCheckComponent.isValidData === 'function') {
            const isValid = this.$refs.customCheckComponent.isValidData()

            if (!isValid) {
              callback(false)
              return
            }

            if (typeof this.$refs.customCheckComponent.getDataToStore === 'function') {
              const validatedData = this.$refs.customCheckComponent.getDataToStore()
              this.parameter.customCheckMethodValidatedData = validatedData
            }

            callback(true)
          } else {
            callback(true)
          }
        } catch (error) {
          console.error('Error al validar componente personalizado:', error)
          callback(false)
        }
      },

      formatTimestamp(timestamp) {
        if (!timestamp) return ''

        // Si el timestamp es un momento de moment.js
        if (this.$moment.isMoment(timestamp)) {
          return timestamp.format('DD/MM/YYYY HH:mm')
        }

        // Si es un timestamp de unix (número)
        if (typeof timestamp === 'number') {
          return this.$moment.unix(timestamp).format('DD/MM/YYYY HH:mm')
        }

        // Si es una fecha válida como string o Date
        if (this.$moment(timestamp).isValid()) {
          return this.$moment(timestamp).format('DD/MM/YYYY HH:mm')
        }

        // Fallback: usar Date como antes si no es válido con moment
        return new Date(timestamp).toLocaleString('es-ES', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      },

      // Método simplificado para guardar comentario
      saveComment() {
        this.$emit('update-comment', this.commentText)
        // El menú se cierra automáticamente al hacer clic fuera
        this.commentText = '' // Limpiar después de guardar
      }
    }
  }
</script>
<style lang="scss" scoped>
  @import "@/assets/styles/resources/colors.scss";

  .comment-btn {
    transition: all 0.2s ease;
    background-color: white;
    width: 24px !important;
    height: 24px !important;
    opacity: 0; // Oculto por defecto
    visibility: hidden;
  }

  .info-btn {
    transition: all 0.2s ease;
    background-color: white;
    width: 24px !important;
    height: 24px !important;
  }

  .param-text {
    max-width: 90%;
    vertical-align: -webkit-baseline-middle;
  }

  .parameter-info {
    background-color: $color_app_contrast_low;

    &.parameter-info-checked {
      .parameter-name {
        margin-bottom: 0;
      }
    }

    &.parameter-info-not-required {
      opacity: 0.7;

      .parameter-name {
        margin-bottom: 0;
      }

      .parameter-description {
        display: none;
      }
    }

    .parameter-name {
      font-weight: 600;
      font-size: 14px;
      color: #333;
      margin-bottom: 4px;
      display: flex;
      align-items: center;
    }

    .parameter-description {
      font-size: 13px;
      color: #666;
      line-height: 1.4;
    }
  }

  .parameter-header {
    padding: 2px 8px 2px 16px;
    border-top-left-radius: 4px;
    border-bottom-left-radius: 4px;

    &:hover {
      background-color: $color_app_hover;

      // Mostrar el botón de comentario solo en hover del header
      .comment-btn {
        opacity: 1;
        visibility: visible;
      }
    }
  }

  .parameter-status-icon {
    transition: all 0.2s ease;
    border-radius: 50%;
    padding: 4px;

    &.clickable {
      cursor: pointer;

      &:hover {
        background-color: rgba(0, 0, 0, 0.08);
        transform: scale(1.1);
      }

      &:active {
        transform: scale(0.95);
      }
    }

    &.readonly {
      cursor: default;
    }
  }

  .parameter-extra-info {
    font-size: 12px;
    border: 1px solid $color_app_contrast_medium;
    border-radius: 4px;
    padding: 8px;
    margin-left: 86px;
    background-color: white !important;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);

    .extra-info-header {
      display: flex;
      align-items: center;
      margin-bottom: 6px;
      font-weight: 600;
      color: #555;

      .extra-info-title {
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
    }

    .extra-info-content {
      .info-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 4px;
        font-family: monospace;

        &:last-child {
          margin-bottom: 0;
        }

        .info-key {
          font-weight: 500;
          color: #333;
          min-width: 80px;
        }

        .info-value {
          color: #666;
          background: white;
          padding: 2px 6px;
          border-radius: 3px;
          border: 1px solid #ddd;
          font-size: 11px;
        }
      }
    }
  }

  .comment-extra-info {
    font-size: 12px;
    color: #666;
    background: #f0f8ff;
    border: 1px solid #b3d9ff;
    border-radius: 4px;
    padding: 8px;

    .comment-content {
      .comment-text {
        font-style: italic;
        color: #333;
        line-height: 1.4;
      }
    }
  }

  .parameter-actions {
    align-self: stretch;
    gap: 4px;
  }

  .revalidate-changes {
    margin: 16px 0;

    .change-comparison {
      margin-bottom: 16px;
      border: 1px solid #e0e0e0;
      border-radius: 4px;
      padding: 12px;

      .field-name {
        font-weight: 600;
        margin-bottom: 8px;
        color: #333;
      }

      .value-comparison {
        display: flex;
        align-items: center;
        gap: 8px;

        .old-value-box, .new-value-box {
          flex: 1;
          padding: 8px;
          border-radius: 4px;
        }

        .old-value-box {
          background: #f5f5f5;
          border: 1px solid #ddd;
        }

        .new-value-box {
          background: #fff3e0;
          border: 1px solid $color_app_warning;
        }

        .value-label {
          font-size: 12px;
          font-weight: 500;
          margin-bottom: 4px;
          color: #666;
        }

        .value-content {
          font-family: monospace;
          font-size: 13px;
          color: #333;
        }

        .arrow {
          color: #666;
        }
      }
    }
  }

  @media (max-width: 768px) {
    .parameter-info {
      padding: 12px;
    }

    .parameter-actions {
      flex-direction: column;
      gap: 8px;
    }

    .extra-info-content .info-item {
      flex-direction: column;
      align-items: flex-start;
      gap: 2px;
    }

    .revalidate-changes .change-comparison .value-comparison {
      flex-direction: column;
      gap: 8px;
    }
  }

  .comment-inline-chip {
    background: #f0f8ff !important;
    border: 1px solid #b3d9ff !important;
    color: #333 !important;
    font-style: italic;

    .v-icon {
      color: #666 !important;
    }
  }

  .parameter-pulse-overlay {
    display: none;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: transparent;
    z-index: -1;
  }

  .parameter-highlight-pulse .parameter-pulse-overlay {
    display: block;
  }

  .button-checklist-status-change {
    min-width: 58px !important;
    justify-content: start;
    padding: 0 4px !important;
  }

</style>